import React, { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import _ from 'lodash';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import AddressAutocomplete from '../../../Components/AddressAutocomplete';

function AddressForm({
  fieldKey, input, handleChange, validationErrors, showCoordWarning, label, inline = false,
}) {
  // Use custom label if provided, otherwise derive from fieldKey
  const displayLabel = label || _.startCase(fieldKey);
  const latitudeKey = fieldKey.replace('address', 'latitude');
  const longitudeKey = fieldKey.replace('address', 'longitude');
  const warningRef = useRef(null);

  const hasCoordinates = _.get(input, latitudeKey);
  const addressValue = _.get(input, fieldKey, '');

  // Check if address seems too vague (just a city/region without specific location)
  // A specific address typically has: street name + number, or is a known POI with multiple commas
  const isAddressTooVague = () => {
    if (!addressValue || !hasCoordinates) return false;

    const parts = addressValue.split(',').map((p) => p.trim());
    // If only 1-2 parts (e.g., "Budapest" or "Budapest, Hungary"), it's likely too vague
    if (parts.length <= 2) return true;

    // Check if first part looks like a street address (contains a number)
    const firstPart = parts[0];
    const hasStreetNumber = /\d/.test(firstPart);
    // Check for common POI indicators (airport, hotel, station, etc.)
    const poiKeywords = /airport|hotel|station|terminal|restaurant|mall|center|centre|museum|hospital|university/i;
    const isPoi = poiKeywords.test(firstPart);

    // If no street number and not a POI, it's likely too vague
    return !hasStreetNumber && !isPoi;
  };

  const addressTooVague = isAddressTooVague();
  const missingCoords = !hasCoordinates;
  const shouldShowWarning = showCoordWarning && (missingCoords || addressTooVague);

  // Scroll to warning when it appears
  useEffect(() => {
    if (shouldShowWarning && warningRef.current) {
      warningRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [shouldShowWarning]);

  const handleAddressChange = (value) => {
    handleChange({ target: { id: fieldKey, value } });
  };

  const handleCoordinatesChange = ({ lat, lon }) => {
    if (lat && lon) {
      handleChange({ target: { id: latitudeKey, value: lat } });
      handleChange({ target: { id: longitudeKey, value: lon } });
    }
  };

  // Inline mode - just render the input fields without table structure
  if (inline) {
    return (
      <div ref={warningRef}>
        <AddressAutocomplete
          id={fieldKey}
          value={_.get(input, fieldKey, '')}
          onChange={handleAddressChange}
          onCoordinatesChange={handleCoordinatesChange}
          className={shouldShowWarning ? 'border-warning bg-warning-subtle' : 'bg-warning-subtle'}
          placeholder="Start typing address..."
        />
        <FormControlFeedback errors={validationErrors} field={fieldKey} />
        {shouldShowWarning && missingCoords && (
          <Form.Text className="text-warning">
            <i className="fa fa-exclamation-triangle me-1" />
            Please select an address from the dropdown to set coordinates
          </Form.Text>
        )}
        {shouldShowWarning && !missingCoords && addressTooVague && (
          <Form.Text className="text-warning">
            <i className="fa fa-exclamation-triangle me-1" />
            Address seems too vague. Please select a specific street address or establishment
          </Form.Text>
        )}
        <div className="d-flex gap-2 mt-1">
          <Form.Control
            id={latitudeKey}
            className="bg-light"
            type="text"
            value={_.get(input, latitudeKey, '')}
            readOnly
            size="sm"
            placeholder="Latitude"
            style={{ flex: 1 }}
          />
          <Form.Control
            id={longitudeKey}
            className="bg-light"
            type="text"
            value={_.get(input, longitudeKey, '')}
            readOnly
            size="sm"
            placeholder="Longitude"
            style={{ flex: 1 }}
          />
        </div>
      </div>
    );
  }

  // Full mode - render with table rows (legacy behavior)
  return (
    <>
      <tr>
        <th className="bg-dark-subtle" colSpan={2}>{displayLabel}</th>
      </tr>
      <tr ref={warningRef}>
        <td className="bg-body-secondary">
          {displayLabel}
          <i className="fa fa-asterisk text-danger bg-body-secondary" />
        </td>
        <td>
          <Form.Group>
            <AddressAutocomplete
              id={fieldKey}
              value={_.get(input, fieldKey, '')}
              onChange={handleAddressChange}
              onCoordinatesChange={handleCoordinatesChange}
              className={shouldShowWarning ? 'border-warning bg-warning-subtle' : 'bg-warning-subtle'}
              placeholder="Start typing address..."
            />
            <FormControlFeedback errors={validationErrors} field={fieldKey} />
            {shouldShowWarning && missingCoords && (
              <Form.Text className="text-warning">
                <i className="fa fa-exclamation-triangle me-1" />
                Please select an address from the dropdown to set coordinates
              </Form.Text>
            )}
            {shouldShowWarning && !missingCoords && addressTooVague && (
              <Form.Text className="text-warning">
                <i className="fa fa-exclamation-triangle me-1" />
                Address seems too vague. Please select a specific street address or establishment
              </Form.Text>
            )}
          </Form.Group>
        </td>
      </tr>
      <tr>
        <td className="bg-body-secondary">Latitude</td>
        <td>
          <Form.Group>
            <Form.Control
              id={latitudeKey}
              className="bg-light"
              type="text"
              value={_.get(input, latitudeKey, '')}
              readOnly
            />
          </Form.Group>
        </td>
      </tr>
      <tr>
        <td className="bg-body-secondary">Longitude</td>
        <td>
          <Form.Group>
            <Form.Control
              id={longitudeKey}
              className="bg-light"
              type="text"
              value={_.get(input, longitudeKey, '')}
              readOnly
            />
          </Form.Group>
        </td>
      </tr>
    </>
  );
}

export default AddressForm;
